import { Scm, ScmEntity } from "@logex/expression-validator";

/**
 * Resolves the root entity of a registry from the SCM `keyedRelations`.
 *
 * `keyedRelations` describe the parent/child hierarchy between SCM entities, referencing them by
 * model name in the form `<entityModelName>.<attribute>`. The root is the entity that is never
 * referenced as a child, i.e. the top of that hierarchy.
 *
 * Returns `null` when the SCM does not yield exactly one root.
 */
export function resolveRootEntity(scm: Scm): ScmEntity | null {
    const entityGroups = scm.childContracts.map(entityGroup => entityGroup.content);
    const entities = entityGroups.flatMap(entityGroup => entityGroup.childContracts);
    const childModelNames = new Set(
        entityGroups
            .flatMap(entityGroup => entityGroup.keyedRelations ?? [])
            .flatMap(relation => relation.childKeys)
            .map(childKey => getEntityModelNameFromKey(childKey))
    );

    const rootEntities = entities.filter(entity => !childModelNames.has(entity.content.modelName));
    return rootEntities.length === 1 ? rootEntities[0] : null;
}

function getEntityModelNameFromKey(key: string): string {
    const separatorIndex = key.lastIndexOf(".");
    return separatorIndex === -1 ? key : key.slice(0, separatorIndex);
}
